import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';

/**
 * One-time native platform setup for Android (and later iOS).
 *
 * - **Back button**: navigates browser history instead of exiting the app.
 * - **Status bar**: white background with dark icons to match the app theme.
 * - **Keyboard**: prevents the WebView layout from being pushed up on Android.
 * - **Splash screen**: hidden after initial render (manual control).
 *
 * All calls are no-ops on web via the `isNativePlatform()` guard.
 * Mount exactly once via `PushNotificationProvider`.
 */
export function useNativePlatform() {
  const navigate = useNavigate();
  const initialised = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || initialised.current) return;
    initialised.current = true;

    // ── Back button ─────────────────────────────────────────────────────
    // Without this, the hardware back button exits the app immediately.
    // Instead, navigate back through React Router history first.
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        navigate(-1);
      } else {
        App.exitApp();
      }
    });

    // ── Status bar ──────────────────────────────────────────────────────
    // White background + dark icons to match the app's light theme.
    StatusBar.setBackgroundColor({ color: '#FFFFFF' }).catch(() => {});
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});

    // ── Keyboard ────────────────────────────────────────────────────────
    // Prevent the soft keyboard from pushing the entire WebView layout up,
    // which causes janky scroll and layout shifts on Android.
    Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => {});

    // ── Splash screen ───────────────────────────────────────────────────
    // Hide the native splash screen now that the WebView has rendered.
    // The Theme.SplashScreen in styles.xml shows while the WebView loads;
    // this manual hide gives us control over the transition timing.
    SplashScreen.hide().catch(() => {});
  }, [navigate]);
}
